import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { ArrowLeft, Activity, Database, Wifi, TrendingUp, Users, MessageSquare, RefreshCw, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SystemMonitorPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadData();
    
    const interval = setInterval(() => {
      if (autoRefresh) {
        loadData();
      }
    }, 10000); // Refresh every 10 seconds
    
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadData = async () => {
    try {
      const [healthRes, metricsRes, statusRes] = await Promise.all([
        axios.get(`${API}/health`),
        axios.get(`${API}/metrics`),
        axios.get(`${API}/system/status`)
      ]);
      setHealth(healthRes.data);
      setMetrics(metricsRes.data);
      setSystemStatus(statusRes.data);
    } catch (error) {
      toast.error('Failed to load system data');
    } finally {
      setLoading(false);
    }
  };

  const clearCache = async () => {
    try {
      await axios.post(`${API}/cache/clear`);
      toast.success('Cache cleared successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear cache');
    }
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2" data-testid="system-monitor-title">
              <Activity className="w-8 h-8" />
              System Monitor
            </h1>
            <p className="text-gray-600">Real-time performance & health monitoring</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={autoRefresh ? "default" : "outline"}
              onClick={() => setAutoRefresh(!autoRefresh)}
              data-testid="auto-refresh-toggle"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto-Refresh: ON' : 'Auto-Refresh: OFF'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/super-admin-dashboard')}
              data-testid="back-to-dashboard-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Health Status */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              System Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="p-4 bg-white rounded-lg border-2 border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Overall Status</span>
                  <Badge variant={health?.status === 'healthy' ? 'default' : 'destructive'} className="bg-green-500">
                    {health?.status}
                  </Badge>
                </div>
              </div>
              
              <div className="p-4 bg-white rounded-lg border-2 border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">MongoDB</span>
                  <Badge variant={health?.services?.mongodb === 'healthy' ? 'default' : 'destructive'} className="bg-green-500">
                    {health?.services?.mongodb}
                  </Badge>
                </div>
              </div>
              
              <div className="p-4 bg-white rounded-lg border-2 border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Redis</span>
                  <Badge variant={health?.services?.redis === 'healthy' ? 'default' : 'secondary'}>
                    {health?.services?.redis}
                  </Badge>
                </div>
              </div>
              
              <div className="p-4 bg-white rounded-lg border-2 border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">WebSocket</span>
                  <Badge variant={health?.services?.websocket === 'healthy' ? 'default' : 'destructive'} className="bg-green-500">
                    {health?.services?.websocket}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Metrics */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4" data-testid="performance-metrics">
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium">System Uptime</span>
                  <span className="text-lg font-bold text-blue-600">
                    {metrics && formatUptime(metrics.uptime_seconds)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium">Total Requests</span>
                  <span className="text-lg font-bold text-green-600">
                    {metrics?.total_requests?.toLocaleString() || 0}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm font-medium">Active WebSocket Connections</span>
                  <span className="text-lg font-bold text-purple-600">
                    {metrics?.active_websocket_connections || 0}
                  </span>
                </div>
                
                {metrics?.redis_stats?.connected_clients !== undefined && (
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                    <span className="text-sm font-medium">Redis Clients</span>
                    <span className="text-lg font-bold text-amber-600">
                      {metrics.redis_stats.connected_clients}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Database Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4" data-testid="database-stats">
                <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg">
                  <span className="text-sm font-medium">Total Users</span>
                  <span className="text-lg font-bold text-indigo-600">
                    {metrics?.database_stats?.total_users?.toLocaleString() || 0}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-pink-50 rounded-lg">
                  <span className="text-sm font-medium">Total Rooms</span>
                  <span className="text-lg font-bold text-pink-600">
                    {metrics?.database_stats?.total_rooms?.toLocaleString() || 0}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-teal-50 rounded-lg">
                  <span className="text-sm font-medium">Total Messages</span>
                  <span className="text-lg font-bold text-teal-600">
                    {metrics?.database_stats?.total_messages?.toLocaleString() || 0}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                  <span className="text-sm font-medium">Total Private Messages</span>
                  <span className="text-lg font-bold text-orange-600">
                    {metrics?.database_stats?.total_pms?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Online Users & System Status */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Online Users ({systemStatus?.online_users?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {systemStatus?.online_users?.map((user) => (
                    <div key={user.id} className="flex items-center gap-3 p-2 bg-green-50 rounded-lg" data-testid={`online-user-${user.id}`}>
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-medium text-sm">{user.username}</span>
                    </div>
                  ))}
                  {(!systemStatus?.online_users || systemStatus.online_users.length === 0) && (
                    <p className="text-center text-gray-500 py-8">No users online</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Recent Rooms</h4>
                    {systemStatus?.recent_rooms?.map((room) => (
                      <div key={room.id} className="flex items-center justify-between p-2 bg-blue-50 rounded mb-2">
                        <span className="text-sm">{room.name}</span>
                        <Badge variant="outline" className="text-xs">{room.type}</Badge>
                      </div>
                    ))}
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Recent Users</h4>
                    {systemStatus?.recent_users?.map((user) => (
                      <div key={user.id} className="p-2 bg-purple-50 rounded mb-2">
                        <span className="text-sm font-medium">{user.username}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Cache Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Cache Management
            </CardTitle>
            <CardDescription>
              Clear Redis cache to free up memory and force data refresh
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              {metrics?.redis_stats?.used_memory_human && (
                <div className="flex-1 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Memory Used</p>
                  <p className="text-2xl font-bold">{metrics.redis_stats.used_memory_human}</p>
                </div>
              )}
              <Button
                variant="destructive"
                onClick={clearCache}
                data-testid="clear-cache-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Cache
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SystemMonitorPage;
